import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Typography,
} from '@mui/material';
import { Pets } from '@mui/icons-material';
import { Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  getWalletTransactions,
  formatCreditsAsRand,
} from '../services/walletService';
import { useWallet } from '../context/WalletContext';
import TopUpDialog from '../components/TopUpDialog';
import { SkeletonBlock, SkeletonCard } from '../components/Skeletons';
import {
  INK, PAPER, PAPER_MUTED, INK_LINE, FONT_DISPLAY, FONT_MONO, moneySx,
} from '../theme/kiff';

const WalletPage = () => {
  const navigate = useNavigate();
  const { balance, refreshWallet } = useWallet();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [topUpOpen, setTopUpOpen] = useState(false);

  const loadLedger = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [ledger] = await Promise.all([
        getWalletTransactions(),
        refreshWallet(),
      ]);

      setTransactions(ledger.results || []);
    } catch (err) {
      console.error('Failed to load wallet:', err);
      setError(err.message || 'Could not load your wallet.');
    } finally {
      setLoading(false);
    }
  }, [refreshWallet]);

  useEffect(() => {
    loadLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (value) =>
    new Date(value).toLocaleString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <SkeletonBlock width={180} height={30} radius={8} sx={{ mb: 1 }} />
        <SkeletonBlock width={320} height={16} sx={{ mb: 4 }} />
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
          <SkeletonCard dark sx={{ flex: '1 1 320px', minHeight: 190 }} />
          <SkeletonCard sx={{ flex: '1 1 320px', minHeight: 190 }} />
        </Box>
        <SkeletonCard sx={{ minHeight: 220 }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h1" component="h1" sx={{ mb: 1 }}>
        Wallet
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Load credits up front, pay for walks without the redirect. 1 credit =
        R1.00.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
        {/* The ink hero: balance as the headline, paw watermark behind it. */}
        <Paper
          sx={{
            flex: '1 1 320px',
            p: 3.5,
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: INK,
            borderColor: INK,
            color: PAPER,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minHeight: 190,
          }}
        >
          <Pets
            aria-hidden
            sx={{
              position: 'absolute',
              right: -28,
              bottom: -34,
              fontSize: 190,
              color: PAPER,
              opacity: 0.08,
              transform: 'rotate(-12deg)',
            }}
          />
          <Typography
            sx={{
              fontFamily: FONT_DISPLAY,
              fontWeight: 800,
              fontSize: 12,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: PAPER_MUTED,
              mb: 1,
            }}
          >
            Available credits
          </Typography>
          <Typography
            component="div"
            sx={{
              fontFamily: FONT_DISPLAY,
              fontWeight: 900,
              fontSize: 36,
              lineHeight: 1,
              letterSpacing: '-0.01em',
              mb: 1,
            }}
          >
            {formatCreditsAsRand(balance)}
          </Typography>
          <Typography
            sx={{ fontFamily: FONT_MONO, fontSize: 12, color: PAPER_MUTED, mb: 2.5 }}
          >
            {balance.toFixed(2)} credits · 1 credit = R1.00
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Plus size={16} />}
              onClick={() => setTopUpOpen(true)}
            >
              Top up
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/payments')}
              sx={{
                color: PAPER,
                borderColor: PAPER,
                '&:hover': { borderColor: PAPER, backgroundColor: INK_LINE },
              }}
            >
              Cards
            </Button>
          </Box>
        </Paper>

        {/* How it works — the honest pitch for pre-loading. */}
        <Paper sx={{ flex: '1 1 320px', p: 3.5, minHeight: 190 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 1.5 }}>
            Pay once, walk many times
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Credits pay for bookings instantly — no redirect at checkout. Top
            up, and you'll be sent to Stitch to pay; the credits appear the
            moment it clears, even if you close the tab.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Anything left over stays in your wallet for the next walk.
          </Typography>
          <Typography sx={{ fontFamily: FONT_MONO, fontSize: 11, color: 'text.disabled' }}>
            Secured by Stitch · instant EFT or card
          </Typography>
        </Paper>
      </Box>

      {/* Ledger */}
      <Typography variant="h2" component="h2" sx={{ mb: 2 }}>
        Credit history
      </Typography>

      {transactions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            No credit activity yet. Load some credits and your history shows up
            here.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Plus size={18} />}
            onClick={() => setTopUpOpen(true)}
          >
            Add credits
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ px: { xs: 2, sm: 3 }, py: 1 }}>
          {transactions.map((tx, i) => (
            <Box
              key={tx.id}
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 2,
                py: 1.75,
                borderTop: i === 0 ? 'none' : '1.5px dashed',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                  {tx.description || tx.transaction_type_display || '—'}
                </Typography>
                <Typography
                  sx={{ fontFamily: FONT_MONO, fontSize: 11, color: 'text.disabled' }}
                >
                  {formatDate(tx.created_at)}
                  {tx.transaction_type_display ? ` · ${tx.transaction_type_display}` : ''}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography
                  sx={{
                    ...moneySx,
                    fontSize: 15,
                    color: tx.is_credit ? 'success.main' : 'error.main',
                  }}
                >
                  {tx.is_credit ? '+' : '−'}R{Math.abs(Number(tx.amount)).toFixed(2)}
                </Typography>
                <Typography
                  sx={{ fontFamily: FONT_MONO, fontSize: 11, color: 'text.disabled' }}
                >
                  bal R{Number(tx.balance_after).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Paper>
      )}

      <TopUpDialog open={topUpOpen} onClose={() => setTopUpOpen(false)} />
    </Container>
  );
};

export default WalletPage;
